/**
 * Distribution Data Hook
 *
 * This hook handles data fetching operations for the distribution feature.
 * It follows the Single Responsibility Principle by focusing only on data operations.
 *
 * @module useDistributionData
 */

import {useCallback} from 'react';
import {useDistributionApplicationService} from './useDistributionApplicationService';
import {useDistributionStore} from '../store/distributionStore';
import StandardizedErrorHandler from '../../../Services/common/StandardizedErrorHandler';
import {DistributionFilters} from '../models/distribution.model';

/**
 * Hook for distribution data operations
 *
 * @returns Object containing data fetching functions and state
 */
export const useDistributionData = () => {
  const applicationService = useDistributionApplicationService();
  const {
    setDistributionHistory,
    setMarketers,
    setProducts,
    setProductAvailableSerials,
    setPagination,
    setLoading,
    setError,
    clearError
  } = useDistributionStore();

  const errorHandlingService = StandardizedErrorHandler.getInstance();

  /**
   * Fetch distribution history
   */
  const fetchDistributionHistory = useCallback(async (filters?: DistributionFilters, page?: number, limit?: number) => {
    try {
      setLoading(true);
      clearError();

      const result = await applicationService.getDistributionHistory(filters, page, limit);
      setDistributionHistory(result.items);
      setPagination(result.pagination);

      return result;
    } catch (error: unknown) {
      const appError = errorHandlingService.toAppError(error);
      setError(appError.message);
      errorHandlingService.handleError(error); // For notifications/logging
      throw error;
    } finally {
      setLoading(false);
    }
  }, [applicationService, setDistributionHistory, setPagination, setLoading, setError, clearError, errorHandlingService]);

  /**
   * Fetch marketers for distribution
   */
  const fetchMarketers = useCallback(async (search?: string) => {
    try {
      setLoading(true);
      clearError();

      const marketers = await applicationService.getMarketersForDistribution(search);
      setMarketers(marketers);

      return marketers;
    } catch (error: unknown) {
      const appError = errorHandlingService.toAppError(error);
      setError(appError.message);
      errorHandlingService.handleError(error); // For notifications/logging
      setMarketers([]);
      throw error;
    } finally {
      setLoading(false);
    }
  }, [applicationService, setMarketers, setLoading, setError, clearError, errorHandlingService]);

  /**
   * Fetch products for distribution
   */
  const fetchProducts = useCallback(async (search?: string) => {
    try {
      setLoading(true);
      clearError();

      const products = await applicationService.getProductsForDistribution(search);
      setProducts(products);

      return products;
    } catch (error: unknown) {
      const appError = errorHandlingService.toAppError(error);
      setError(appError.message);
      errorHandlingService.handleError(error); // For notifications/logging
      setProducts([]);
      throw error;
    } finally {
      setLoading(false);
    }
  }, [applicationService, setProducts, setLoading, setError, clearError, errorHandlingService]);

  /**
   * Fetch available serials for a product
   */
  const fetchAvailableSerials = useCallback(async (productId: string) => {
    try {
      setLoading(true);
      clearError();

      const serials = await applicationService.getAvailableSerials(productId);
      setProductAvailableSerials(serials);

      return serials;
    } catch (error: unknown) {
      const appError = errorHandlingService.toAppError(error);
      setError(appError.message);
      errorHandlingService.handleError(error); // For notifications/logging
      setProductAvailableSerials([]);
      throw error;
    } finally {
      setLoading(false);
    }
  }, [applicationService, setProductAvailableSerials, setLoading, setError, clearError, errorHandlingService]);

  /**
   * Clear available serials
   */
  const clearAvailableSerials = useCallback(() => {
    setProductAvailableSerials([]);
  }, [setProductAvailableSerials]);

  return {
    fetchDistributionHistory,
    fetchMarketers,
    fetchProducts,
    fetchAvailableSerials,
    clearAvailableSerials
  };
};
